import { useState, useEffect } from 'react';
import { Plus, Search, Filter, Edit2, Trash2, Copy, History, FileText, Zap, Globe, Printer, Phone, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { OpsLayout } from '@/components/ops/OpsLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { usePayerRules } from '@/hooks/usePayerRules';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  AUTH_REQUIREMENT_LABELS,
  AUTH_REQUIREMENT_COLORS,
  SUBMISSION_LANE_LABELS,
  INJURY_TYPE_LABELS,
  SITE_OF_SERVICE_LABELS,
  type PayerAuthRule,
  type RuleFormInput,
  type AuthRequirement,
  type SubmissionLaneType,
  type InjuryType,
  type SiteOfService,
} from '@/lib/rules/types';

interface Payer {
  id: string;
  name: string;
  payer_id: string;
}

export default function PayerRulesAdmin() {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPayer, setFilterPayer] = useState<string>('all');
  const [filterInjuryType, setFilterInjuryType] = useState<string>('all');
  const [payers, setPayers] = useState<Payer[]>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<PayerAuthRule | null>(null);
  
  const { 
    rules, 
    templates, 
    documentTypes, 
    loading, 
    error, 
    fetchRules, 
    fetchTemplates, 
    fetchDocumentTypes,
    createRule,
    updateRule,
    deactivateRule,
  } = usePayerRules();
  
  const { toast } = useToast();

  useEffect(() => {
    fetchRules();
    fetchTemplates();
    fetchDocumentTypes();
    fetchPayers();
  }, [fetchRules, fetchTemplates, fetchDocumentTypes]);

  const fetchPayers = async () => {
    const { data } = await supabase.from('payers').select('id, name, payer_id').order('name');
    if (data) setPayers(data);
  };

  const handleFilterChange = () => {
    const filters: { payer_id?: string; injury_type?: 'bwc' | 'commercial'; cpt_code?: string } = {};
    if (filterPayer !== 'all') filters.payer_id = filterPayer;
    if (filterInjuryType !== 'all') filters.injury_type = filterInjuryType as 'bwc' | 'commercial';
    if (searchTerm) filters.cpt_code = searchTerm;
    fetchRules(filters);
  };

  useEffect(() => {
    handleFilterChange();
  }, [filterPayer, filterInjuryType]);

  const handleSearch = () => {
    handleFilterChange();
  };

  const handleDeactivate = async (ruleId: string) => {
    const success = await deactivateRule(ruleId);
    if (success) {
      toast({ title: 'Rule deactivated', description: 'The rule has been deactivated successfully.' });
      fetchRules();
    } else {
      toast({ title: 'Error', description: 'Failed to deactivate rule.', variant: 'destructive' });
    }
  };

  const getSubmissionLaneIcon = (lane: SubmissionLaneType) => {
    const icons = {
      fhir_pas: <Zap className="h-4 w-4 text-primary" />,
      portal: <Globe className="h-4 w-4 text-blue-500" />,
      fax: <Printer className="h-4 w-4 text-amber-500" />,
      phone: <Phone className="h-4 w-4 text-purple-500" />,
    };
    return icons[lane];
  };

  const getAuthBadge = (auth: AuthRequirement) => (
    <Badge variant="outline" className={AUTH_REQUIREMENT_COLORS[auth]}>
      {AUTH_REQUIREMENT_LABELS[auth]}
    </Badge>
  );

  const filteredRules = rules.filter(rule => {
    if (searchTerm && !rule.cpt_code.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    return true;
  });

  return (
    <OpsLayout>
      <div className="w-full max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Payer Rules Engine</h1>
            <p className="text-muted-foreground mt-2">
              Manage authorization rules by payer, CPT code, and injury type
            </p>
          </div>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Rule
              </Button>
            </DialogTrigger>
            <RuleFormDialog
              payers={payers}
              documentTypes={documentTypes}
              templates={templates}
              onSave={async (input) => {
                const result = await createRule(input);
                if (result) {
                  toast({ title: 'Rule created', description: 'The new rule has been created successfully.' });
                  setIsCreateDialogOpen(false);
                  fetchRules();
                }
              }}
              onCancel={() => setIsCreateDialogOpen(false)}
            />
          </Dialog>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-2xl">{rules.length}</CardTitle>
              <CardDescription>Active Rules</CardDescription>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-2xl">{rules.filter(r => r.injury_type === 'bwc').length}</CardTitle>
              <CardDescription>BWC Rules</CardDescription>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-2xl">{rules.filter(r => r.injury_type === 'commercial').length}</CardTitle>
              <CardDescription>Commercial Rules</CardDescription>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-2xl">{templates.length}</CardTitle>
              <CardDescription>Templates</CardDescription>
            </CardHeader>
          </Card>
        </div>

        <Tabs defaultValue="rules" className="space-y-6">
          <TabsList>
            <TabsTrigger value="rules">Authorization Rules</TabsTrigger>
            <TabsTrigger value="templates">Rule Templates</TabsTrigger>
            <TabsTrigger value="documents">Document Types</TabsTrigger>
          </TabsList>

          {/* Rules Tab */}
          <TabsContent value="rules" className="space-y-4">
            {/* Filters */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-wrap gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <Input
                      placeholder="Search by CPT code..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    />
                  </div>
                  <Select value={filterPayer} onValueChange={setFilterPayer}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Filter by payer" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Payers</SelectItem>
                      {payers.map(payer => (
                        <SelectItem key={payer.id} value={payer.id}>{payer.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filterInjuryType} onValueChange={setFilterInjuryType}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Filter by injury type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Injury Types</SelectItem>
                      <SelectItem value="bwc">Ohio BWC</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={handleSearch}>
                    <Search className="h-4 w-4 mr-2" />
                    Search
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Rules Table */}
            <Card>
              <CardContent className="p-0">
                <ScrollArea className="h-[600px]">
                  <Table>
                    <TableHeader className="sticky top-0 bg-background">
                      <TableRow>
                        <TableHead>CPT</TableHead>
                        <TableHead>Mod</TableHead>
                        <TableHead>Payer</TableHead>
                        <TableHead>Injury Type</TableHead>
                        <TableHead>Auth Required</TableHead>
                        <TableHead>Submission</TableHead>
                        <TableHead>SLA</TableHead>
                        <TableHead>Docs</TableHead>
                        <TableHead>Version</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                            Loading rules...
                          </TableCell>
                        </TableRow>
                      ) : filteredRules.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                            No rules found. Create your first rule to get started.
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredRules.map((rule) => (
                          <TableRow key={rule.id}>
                            <TableCell className="font-mono font-medium">{rule.cpt_code}</TableCell>
                            <TableCell>{rule.modifier || '—'}</TableCell>
                            <TableCell className="max-w-[150px] truncate">
                              {rule.payer?.name || 'All Payers'}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={rule.injury_type === 'bwc' ? 'bg-orange-500/10 text-orange-500' : ''}>
                                {INJURY_TYPE_LABELS[rule.injury_type]}
                              </Badge>
                            </TableCell>
                            <TableCell>{getAuthBadge(rule.auth_requirement)}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                {getSubmissionLaneIcon(rule.primary_submission_lane)}
                                <span className="text-xs">{SUBMISSION_LANE_LABELS[rule.primary_submission_lane]}</span>
                              </div>
                            </TableCell>
                            <TableCell>{Math.round(rule.sla_hours / 24)}d</TableCell>
                            <TableCell>
                              <Badge variant="secondary">{rule.required_documents?.length || 0}</Badge>
                            </TableCell>
                            <TableCell>v{rule.version}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setEditingRule(rule)}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeactivate(rule.id)}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => (
                <Card key={template.id} className={template.is_system_template ? 'border-primary/50' : ''}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{template.name}</CardTitle>
                      {template.is_system_template && (
                        <Badge variant="secondary">System</Badge>
                      )}
                    </div>
                    <CardDescription>{template.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Injury Type</span>
                      <Badge variant="outline">{INJURY_TYPE_LABELS[template.injury_type]}</Badge>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Auth Required</span>
                      {getAuthBadge(template.auth_requirement)}
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Submission Lane</span>
                      <div className="flex items-center gap-1">
                        {getSubmissionLaneIcon(template.primary_submission_lane)}
                        <span>{SUBMISSION_LANE_LABELS[template.primary_submission_lane]}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">SLA</span>
                      <span>{Math.round(template.sla_hours / 24)} days</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Required Docs</span>
                      <Badge variant="secondary">{template.required_documents?.length || 0}</Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Document Types Tab */}
          <TabsContent value="documents" className="space-y-4">
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Common</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {documentTypes.map((docType) => (
                      <TableRow key={docType.id}>
                        <TableCell className="font-mono">{docType.code}</TableCell>
                        <TableCell className="font-medium">{docType.name}</TableCell>
                        <TableCell className="max-w-[300px] text-muted-foreground">{docType.description}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{docType.category}</Badge>
                        </TableCell>
                        <TableCell>
                          {docType.is_common ? (
                            <CheckCircle className="h-4 w-4 text-primary" />
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Dialog */}
        {editingRule && (
          <Dialog open={!!editingRule} onOpenChange={() => setEditingRule(null)}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <RuleFormDialog
                payers={payers}
                documentTypes={documentTypes}
                templates={templates}
                initialData={editingRule}
                onSave={async (input) => {
                  const result = await updateRule(editingRule.id, input);
                  if (result) {
                    toast({ title: 'Rule updated', description: 'A new version of the rule has been created.' });
                    setEditingRule(null);
                    fetchRules();
                  }
                }}
                onCancel={() => setEditingRule(null)}
              />
            </DialogContent>
          </Dialog>
        )}

        {error && (
          <div className="mt-4 bg-destructive/10 border border-destructive/30 rounded-lg p-4 text-destructive">
            {error}
          </div>
        )}
      </div>
    </OpsLayout>
  );
}

// Rule Form Dialog Component
function RuleFormDialog({
  payers,
  documentTypes,
  templates,
  initialData,
  onSave,
  onCancel,
}: {
  payers: Payer[];
  documentTypes: { id: string; code: string; name: string; category?: string }[];
  templates: { id: string; name: string; injury_type: string; required_documents: string[] }[];
  initialData?: PayerAuthRule;
  onSave: (input: RuleFormInput) => Promise<void>;
  onCancel: () => void;
}) {
  const [formData, setFormData] = useState<Partial<RuleFormInput>>({
    payer_id: initialData?.payer_id,
    plan_name: initialData?.plan_name,
    cpt_code: initialData?.cpt_code || '',
    modifier: initialData?.modifier,
    injury_type: initialData?.injury_type || 'commercial',
    site_of_service: initialData?.site_of_service,
    auth_requirement: initialData?.auth_requirement || 'required',
    auth_condition_description: initialData?.auth_condition_description,
    primary_submission_lane: initialData?.primary_submission_lane || 'portal',
    fallback_submission_lane: initialData?.fallback_submission_lane,
    sla_hours: initialData?.sla_hours || 336,
    urgent_sla_hours: initialData?.urgent_sla_hours || 72,
    initial_follow_up_hours: initialData?.initial_follow_up_hours || 72,
    follow_up_cadence_hours: initialData?.follow_up_cadence_hours || 48,
    required_documents: initialData?.required_documents || [],
    clinical_criteria: initialData?.clinical_criteria,
    special_instructions: initialData?.special_instructions,
    effective_date: initialData?.effective_date || new Date().toISOString().split('T')[0],
  });

  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!formData.cpt_code) return;
    
    setSaving(true);
    try {
      await onSave(formData as RuleFormInput);
    } finally {
      setSaving(false);
    }
  };

  const handleTemplateApply = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setFormData(prev => ({
        ...prev,
        required_documents: template.required_documents || [],
      }));
    }
  };

  const toggleDocument = (code: string) => {
    setFormData(prev => ({
      ...prev,
      required_documents: prev.required_documents?.includes(code)
        ? prev.required_documents.filter(d => d !== code)
        : [...(prev.required_documents || []), code],
    }));
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{initialData ? 'Edit Rule' : 'Create New Rule'}</DialogTitle>
        <DialogDescription>
          Configure authorization requirements for a specific CPT code and payer combination.
        </DialogDescription>
      </DialogHeader>

      <div className="grid gap-6 py-4">
        {/* Matching Criteria */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium">Matching Criteria</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cpt_code">CPT Code *</Label>
              <Input
                id="cpt_code"
                value={formData.cpt_code}
                onChange={(e) => setFormData(prev => ({ ...prev, cpt_code: e.target.value }))}
                placeholder="e.g., 27447"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="modifier">Modifier</Label>
              <Input
                id="modifier"
                value={formData.modifier || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, modifier: e.target.value || undefined }))}
                placeholder="e.g., 26, TC"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="payer">Payer</Label>
              <Select 
                value={formData.payer_id || 'all'} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, payer_id: v === 'all' ? undefined : v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Payers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Payers</SelectItem>
                  {payers.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="injury_type">Injury Type *</Label>
              <Select 
                value={formData.injury_type} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, injury_type: v as InjuryType }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="commercial">Commercial</SelectItem>
                  <SelectItem value="bwc">Ohio BWC</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="site">Site of Service</Label>
              <Select 
                value={formData.site_of_service || 'any'} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, site_of_service: v === 'any' ? undefined : v as SiteOfService }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="hospital">Hospital</SelectItem>
                  <SelectItem value="asc">ASC</SelectItem>
                  <SelectItem value="office">Office</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Authorization Settings */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium">Authorization Settings</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Auth Requirement *</Label>
              <Select 
                value={formData.auth_requirement} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, auth_requirement: v as AuthRequirement }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="required">Required</SelectItem>
                  <SelectItem value="not_required">Not Required</SelectItem>
                  <SelectItem value="conditional">Conditional</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Submission Lane *</Label>
              <Select 
                value={formData.primary_submission_lane} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, primary_submission_lane: v as SubmissionLaneType }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fhir_pas">FHIR PAS (Electronic)</SelectItem>
                  <SelectItem value="portal">Payer Portal</SelectItem>
                  <SelectItem value="fax">Fax</SelectItem>
                  <SelectItem value="phone">Phone</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {formData.auth_requirement === 'conditional' && (
            <div className="space-y-2">
              <Label>Condition Description</Label>
              <Textarea
                value={formData.auth_condition_description || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, auth_condition_description: e.target.value }))}
                placeholder="e.g., Required if patient is under 65 or has prior surgery"
              />
            </div>
          )}
        </div>

        {/* SLA & Follow-up */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium">SLA & Follow-up</h3>
          <div className="grid grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>SLA (hours)</Label>
              <Input
                type="number"
                value={formData.sla_hours}
                onChange={(e) => setFormData(prev => ({ ...prev, sla_hours: parseInt(e.target.value) || 336 }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Urgent SLA (hours)</Label>
              <Input
                type="number"
                value={formData.urgent_sla_hours}
                onChange={(e) => setFormData(prev => ({ ...prev, urgent_sla_hours: parseInt(e.target.value) || 72 }))}
              />
            </div>
            <div className="space-y-2">
              <Label>First Follow-up (hours)</Label>
              <Input
                type="number"
                value={formData.initial_follow_up_hours}
                onChange={(e) => setFormData(prev => ({ ...prev, initial_follow_up_hours: parseInt(e.target.value) || 72 }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Follow-up Cadence (hours)</Label>
              <Input
                type="number"
                value={formData.follow_up_cadence_hours}
                onChange={(e) => setFormData(prev => ({ ...prev, follow_up_cadence_hours: parseInt(e.target.value) || 48 }))}
              />
            </div>
          </div>
        </div>

        {/* Documentation Requirements */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">Required Documents</h3>
            <Select onValueChange={handleTemplateApply}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Apply template..." />
              </SelectTrigger>
              <SelectContent>
                {templates.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto">
            {documentTypes.map(doc => (
              <div key={doc.id} className="flex items-center space-x-2">
                <Checkbox
                  id={doc.code}
                  checked={formData.required_documents?.includes(doc.code)}
                  onCheckedChange={() => toggleDocument(doc.code)}
                />
                <Label htmlFor={doc.code} className="text-sm cursor-pointer">
                  {doc.name}
                </Label>
              </div>
            ))}
          </div>
        </div>

        {/* Additional Info */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium">Additional Information</h3>
          <div className="space-y-2">
            <Label>Clinical Criteria</Label>
            <Textarea
              value={formData.clinical_criteria || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, clinical_criteria: e.target.value }))}
              placeholder="Clinical criteria for medical necessity..."
            />
          </div>
          <div className="space-y-2">
            <Label>Special Instructions</Label>
            <Textarea
              value={formData.special_instructions || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, special_instructions: e.target.value }))}
              placeholder="Any special handling instructions..."
            />
          </div>
        </div>

        {/* Effective Date */}
        <div className="space-y-2">
          <Label>Effective Date *</Label>
          <Input
            type="date"
            value={formData.effective_date}
            onChange={(e) => setFormData(prev => ({ ...prev, effective_date: e.target.value }))}
          />
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={saving || !formData.cpt_code}>
          {saving ? 'Saving...' : initialData ? 'Save New Version' : 'Create Rule'}
        </Button>
      </DialogFooter>
    </>
  );
}
